import { useState, useEffect } from "react";
import { usersApi } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Users, UserPlus, Shield, Trash2, KeyRound, Loader2, X, Check } from "lucide-react";
import { toast } from "sonner";

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetUser, setResetUser] = useState(null);

  useEffect(() => { loadUsers(); }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      const res = await usersApi.getAll({ includeInactive: true });
      setUsers(res.data.users || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  async function handleDeactivate(u) {
    if (!window.confirm(`Deactivate ${u.name || u.email}? They will no longer be able to log in.`)) return;
    try {
      await usersApi.delete(u.id);
      toast.success(`${u.name || u.email} deactivated`);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to deactivate");
    }
  }

  const isSuper = me?.role === "super_admin";
  const isAdminLike = isSuper || me?.role === "admin";
  if (!isAdminLike) {
    return (
      <div className="p-8 text-center text-[--text-muted]">
        Only admins and super-admins can manage users.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="users-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-extrabold text-[--text-primary] flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Users size={22} className="text-[--accent]" /> User Management
          </h1>
          <p className="text-xs text-[--text-muted] mt-1">Create, edit, deactivate, and reset passwords for CRM users.</p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-white"
          style={{ background: "linear-gradient(135deg, #D97706, #F59E0B)" }}
          data-testid="create-user-btn"
        >
          <UserPlus size={14} /> Create User
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[--accent]" /></div>
      ) : (
        <div className="bg-white border border-[--border] rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-[--bg-cream] text-[10px] uppercase tracking-wider text-[--text-muted]">
              <tr>
                <th className="text-left px-4 py-3 font-bold">Name</th>
                <th className="text-left px-4 py-3 font-bold">Email</th>
                <th className="text-left px-4 py-3 font-bold">Role</th>
                <th className="text-left px-4 py-3 font-bold">Department</th>
                <th className="text-left px-4 py-3 font-bold">Country</th>
                <th className="text-left px-4 py-3 font-bold">Status</th>
                <th className="text-right px-4 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className="border-t border-[--border] hover:bg-amber-50/30" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-semibold text-[--text-primary]">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-[--text-muted] font-mono text-[12px]">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      u.role === "super_admin" ? "bg-purple-100 text-purple-800" :
                      u.role === "admin" ? "bg-amber-100 text-amber-800" :
                      u.role === "leader" ? "bg-blue-100 text-blue-800" :
                      "bg-gray-100 text-gray-700"
                    }`}>{u.role}</span>
                  </td>
                  <td className="px-4 py-3 text-[--text-muted]">{u.department || "—"}</td>
                  <td className="px-4 py-3 text-[--text-muted] text-[11px]">
                    {Array.isArray(u.country) ? u.country.join(", ") : (u.country || "—")}
                  </td>
                  <td className="px-4 py-3">
                    {u.is_active ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Active
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">Deactivated</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1.5">
                      <button
                        onClick={() => setEditUser(u)}
                        className="text-[11px] font-bold text-blue-600 hover:bg-blue-50 px-2 py-1 rounded-full"
                        data-testid={`edit-user-${u.id}`}
                        title="Edit role / department / country"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setResetUser(u)}
                        className="text-[11px] font-bold text-amber-700 hover:bg-amber-50 px-2 py-1 rounded-full inline-flex items-center gap-1"
                        data-testid={`reset-pw-${u.id}`}
                        title="Reset this user's password"
                      >
                        <KeyRound size={10} /> Reset PW
                      </button>
                      {u.is_active && u.id !== me?.id && u.role !== "super_admin" && (
                        <button
                          onClick={() => handleDeactivate(u)}
                          className="text-[11px] font-bold text-red-600 hover:bg-red-50 px-2 py-1 rounded-full inline-flex items-center gap-1"
                          data-testid={`deactivate-${u.id}`}
                          title="Deactivate user"
                        >
                          <Trash2 size={10} /> Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan="7" className="text-center py-12 text-[--text-muted]">No users yet. Click <b>Create User</b> to add one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateUserModal
          isSuper={isSuper}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadUsers(); }}
        />
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          isSuper={isSuper}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); loadUsers(); }}
        />
      )}
      {resetUser && (
        <ResetPasswordModal
          user={resetUser}
          onClose={() => setResetUser(null)}
          onDone={() => setResetUser(null)}
        />
      )}
    </div>
  );
}

function CreateUserModal({ onClose, onCreated, isSuper }) {
  const [form, setForm] = useState({
    email: "", password: "", name: "",
    role: "salesperson", department: "Sales", country: ["India"],
  });
  const [saving, setSaving] = useState(false);

  const toggleCountry = (c) => {
    setForm(f => ({
      ...f,
      country: f.country.includes(c) ? f.country.filter(x => x !== c) : [...f.country, c],
    }));
  };

  async function submit(e) {
    e.preventDefault();
    if (!form.email.trim() || !form.password || !form.name.trim()) {
      toast.error("Name, email, and password are required");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!form.country.length) {
      toast.error("Select at least one country");
      return;
    }
    setSaving(true);
    try {
      const res = await usersApi.create(form);
      toast.success(res.data?.message || "User created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="create-user-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[--border] flex items-center justify-between" style={{ background: "linear-gradient(135deg, #FEF3C7, #FDE68A)" }}>
          <div className="flex items-center gap-2">
            <UserPlus size={18} className="text-amber-800" />
            <h3 className="text-[15px] font-extrabold text-amber-900">Create New User</h3>
          </div>
          <button type="button" onClick={onClose} className="text-amber-700 hover:text-amber-900"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="new-user-name" /></Field>
          <Field label="Email"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input font-mono" data-testid="new-user-email" /></Field>
          <Field label="Password"><input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" className="input font-mono" data-testid="new-user-password" /></Field>
          <Field label="Role">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input" data-testid="new-user-role">
              <option value="salesperson">Salesperson</option>
              <option value="leader">Leader (department head)</option>
              {isSuper && <option value="admin">Admin</option>}
            </select>
          </Field>
          <Field label="Department">
            <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="input" data-testid="new-user-dept">
              <option value="Sales">Sales</option>
              <option value="Customer Support">Customer Support</option>
            </select>
          </Field>
          <Field label="Country access">
            <div className="flex gap-2">
              {["India", "US"].map(c => (
                <button
                  type="button"
                  key={c}
                  onClick={() => toggleCountry(c)}
                  className={`flex-1 py-2 rounded-lg text-[12px] font-bold border-2 transition-all ${
                    form.country.includes(c)
                      ? "bg-amber-50 border-amber-300 text-amber-900"
                      : "bg-white border-[--border] text-[--text-muted] hover:border-amber-200"
                  }`}
                  data-testid={`new-user-country-${c.toLowerCase()}`}
                >
                  {form.country.includes(c) && <Check size={12} className="inline mr-1" />}{c}
                </button>
              ))}
            </div>
          </Field>
        </div>
        <div className="px-5 py-3 border-t border-[--border] flex justify-end gap-2 bg-[--bg-cream]">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold text-[--text-muted] hover:bg-gray-100">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 rounded-full text-sm font-bold text-white flex items-center gap-2 disabled:opacity-50" style={{ background: "linear-gradient(135deg, #D97706, #F59E0B)" }} data-testid="submit-create-user">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Create
          </button>
        </div>
      </form>
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved, isSuper }) {
  const [form, setForm] = useState({
    name: user.name || "",
    role: user.role || "salesperson",
    department: user.department || "Sales",
    country: Array.isArray(user.country) ? user.country : (user.country ? [user.country] : ["India"]),
    is_active: user.is_active !== false,
  });
  const [saving, setSaving] = useState(false);

  const toggleCountry = (c) => {
    setForm(f => ({ ...f, country: f.country.includes(c) ? f.country.filter(x => x !== c) : [...f.country, c] }));
  };

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await usersApi.update(user.id, form);
      toast.success("User updated");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="edit-user-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[--border] flex items-center justify-between bg-blue-50">
          <h3 className="text-[15px] font-extrabold text-blue-900 flex items-center gap-2"><Shield size={18} /> Edit {user.email}</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" /></Field>
          <Field label="Role">
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input" disabled={user.role === "super_admin"}>
              <option value="salesperson">Salesperson</option>
              <option value="leader">Leader</option>
              {(isSuper || user.role === "admin") && <option value="admin">Admin</option>}
              {user.role === "super_admin" && <option value="super_admin">Super Admin</option>}
            </select>
          </Field>
          <Field label="Department">
            <select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="input">
              <option value="Sales">Sales</option>
              <option value="Customer Support">Customer Support</option>
            </select>
          </Field>
          <Field label="Country access">
            <div className="flex gap-2">
              {["India", "US"].map(c => (
                <button type="button" key={c} onClick={() => toggleCountry(c)}
                  className={`flex-1 py-2 rounded-lg text-[12px] font-bold border-2 transition-all ${
                    form.country.includes(c)
                      ? "bg-blue-50 border-blue-300 text-blue-900"
                      : "bg-white border-[--border] text-[--text-muted]"
                  }`}>{form.country.includes(c) && <Check size={12} className="inline mr-1" />}{c}</button>
              ))}
            </div>
          </Field>
          <label className="flex items-center gap-2 text-[13px] text-[--text-primary]">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Active (can log in)
          </label>
        </div>
        <div className="px-5 py-3 border-t border-[--border] flex justify-end gap-2 bg-[--bg-cream]">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold text-[--text-muted] hover:bg-gray-100">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 rounded-full text-sm font-bold text-white flex items-center gap-2 disabled:opacity-50 bg-blue-600 hover:bg-blue-700" data-testid="submit-edit-user">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save
          </button>
        </div>
      </form>
    </div>
  );
}

function ResetPasswordModal({ user, onClose, onDone }) {
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSaving(true);
    try {
      await usersApi.resetPassword(user.id, password);
      toast.success(`Password reset for ${user.email}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="reset-pw-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[--border] flex items-center justify-between bg-amber-50">
          <h3 className="text-[15px] font-extrabold text-amber-900 flex items-center gap-2"><KeyRound size={18} /> Reset Password</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[12px] text-[--text-muted]">Set a new password for <b className="text-[--text-primary]">{user.email}</b>. Share it with them over a secure channel.</p>
          <Field label="New password">
            <input required type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" className="input font-mono" data-testid="new-password-input" autoFocus />
          </Field>
        </div>
        <div className="px-5 py-3 border-t border-[--border] flex justify-end gap-2 bg-[--bg-cream]">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold text-[--text-muted] hover:bg-gray-100">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 rounded-full text-sm font-bold text-white flex items-center gap-2 disabled:opacity-50" style={{ background: "linear-gradient(135deg, #D97706, #F59E0B)" }} data-testid="submit-reset-pw">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Reset
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold text-[--text-muted] uppercase tracking-wider block mb-1">{label}</span>
      {children}
    </label>
  );
}
